// ============================================================================
// CRM IMPACT STRIP — all-time Studio CRM headline figures shown above the
// Studio TV player for people allowed to see commercial impact
// ============================================================================

import React, { useEffect, useState } from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { CrmImpact, getCrmImpact } from '../../services/studio-crm';
import MoneyCard, { COMPACT_CARD, COMPACT_LABEL } from '../studio-crm/MoneyCard';

// A percentage from one or two closed deals reads as more certain than it is
const MIN_CLOSED_FOR_WIN_RATE = 5;

export default function CrmImpactStrip() {
  const [impact, setImpact] = useState<CrmImpact | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCrmImpact()
      .then(data => { if (!cancelled) setImpact(data); })
      .catch(() => { if (!cancelled) setError('Could not load studio impact figures'); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-red-600">
        <AlertCircle size={14} /> {error}
      </div>
    );
  }
  if (!impact) return null;

  const { coverage } = impact;
  const pct = coverage.past > 0 ? Math.round((coverage.linked / coverage.past) * 100) : 0;
  const winRate = impact.winRate !== null && impact.wonCount + impact.lostCount >= MIN_CLOSED_FOR_WIN_RATE
    ? `${Math.round(impact.winRate * 100)}%` : null;
  const rates = impact.fxRates?.rates || null;

  return (
    <section className="flex flex-col gap-2 min-w-0 xl:flex-[10]">
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
        Studio impact, all time
        <span title={'Past sessions linked: approved sessions to date, excluding bookings marked No CRM needed. '
          + 'Pipeline and won TCV: every linked opportunity, counted once even if linked to several bookings, '
          + 'the same as the Studio CRM screen with no filters. ≈ EUR totals use fixed reporting rates. '
          + 'Attribution is entered by facilitators, so figures show association with sessions, not proof of cause.'}>
          <Info size={13} className="text-slate-400 cursor-help" />
        </span>
      </div>
      <div className="flex flex-wrap md:flex-nowrap items-stretch gap-3 flex-1">
        <div className={`${COMPACT_CARD} justify-between`}>
          <span className={COMPACT_LABEL}>Past sessions linked</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-xl font-bold text-navy">{coverage.linked} of {coverage.past}</span>
            <span className="text-sm font-semibold text-primary">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <MoneyCard label="Open pipeline" totals={impact.openPipeline} split={impact.pipelineSplit} rates={rates} compact
          footer={`${impact.openCount} open ${impact.openCount === 1 ? 'opportunity' : 'opportunities'}`} />
        <MoneyCard label="Won TCV" totals={impact.wonTcv} split={impact.wonSplit} rates={rates} compact
          footer={`${impact.wonCount} won · ${impact.lostCount} lost${winRate ? ` · ${winRate} win rate` : ''}`} />
      </div>
    </section>
  );
}
